import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserSelectionsService } from '../user-selections/user-selections.service';
import { ProductService } from '../product/product.service';
import { Product } from '../../types/product.type';
import { Observable, combineLatest, Subscription  } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import { AppSettings } from '../../PRE-LOAD/app-settings';
import { AnalyticsService } from '../analytics/analytics.service';

@Injectable({
  providedIn: 'root'
})
export class DeepLinkService {

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private appSettings: AppSettings,
        private userSelectionService: UserSelectionsService,
        private productService: ProductService,
        private analyticsService: AnalyticsService
    ) {
        this.handleQueryParams();
    }

    /**
     * Applies business logic against the set query params.
     *
     * The problem we're trying to solve here is how to appropriately respond
     * to the given query params. If for example, we only receive a 'model' parameter,
     * them we simply load the model/series and navigate to the select-appointment route.
     * But if we receive both a model AND a time property, then we need to check the vehicle
     * exists AND check the time is available and if both are true then navigate to the
     * personal information route. Throughout all of this we need to respond to incorrect values
     * being set against each param. E.g What happens if we recieve a model param but the value
     * is not recognised.
     */
    private handleQueryParams() {
        this.activatedRoute.queryParams.pipe(
            takeWhile(queryParams => !queryParams.model, true)
        ).subscribe(queryParams => {
            // If the only query param we recieve is the model, then handle the value.
            // There are ways of handling a model.
            // 1. If the model is valid and is able to be booked for test drive.
            //    - Redirect to the select-appointment route.
            // 2. If the model is valid but is not able to be booked for test drive.
            //    - Navigate to the old booking form.
            // 3. If the model is invalid.
            //    - Navigate to the model-select route.
            if (queryParams.model) {
                // Start by create an observable which when subscribed to, returns a vehicle and
                // its isActive value or null if it doesn't exist.
                const obs = combineLatest(
                    this.getVehicle(queryParams.model), // Vehicle's avaliable in the app from listings.json
                ).pipe(
                    map(data => {
                        // What we're doing here is subscribing the the latest value emited from the
                        // this.getVehicle(queryParams.model) AND this.vehicleActive(queryParams.model)
                        // observables.
                        // @see https://www.learnrxjs.io/learn-rxjs/operators/combination/combinelatest
                        const vehicle = data[0];
                        if (vehicle) {
                            return vehicle;
                        } else {
                            return null;
                        }
                    })
                ).subscribe((vehicle: Product) => {
                    // If we found a vehicle that matches the query param AND the
                    // vehicle is active (available for real-time booking) then
                    // update user selections and navigate to the appointment page.
                    if (vehicle && vehicle.enabled) {
                        this.userSelectionService.set('product', vehicle);
                        this.userSelectionService.set('modelTreeRef', queryParams.model);
                        this.analyticsService.setModel();
                        // @todo Why does this only work using a setTimeout(). Without a setTimeout()
                        // we arrive at the select-appointment component before the product is set above.
                        setTimeout(() => {
                            this.router.navigate(['select-appointment'], {
                                skipLocationChange: true
                            });
                        }, 100);
                    }

                    // If we found a vehicle that matches the query param AND the
                    // vehicle is NOT active (not available for real-time booking) then
                    // navigate to the old booking form.
                    if (vehicle && !vehicle.enabled) {
                        window.location.href = this.buildOldFormUrl(vehicle);
                    }

                    // If the vehicle/model is not valid, navigate to the select-model route for the user
                    // to pick their model.
                    if (!vehicle) {
                        this.router.navigate(['select-model'], {
                            skipLocationChange: true
                        });
                    }
                });
            }
        });
    }

    /**
     * Given a vehicle identifier, return an observable of vehicle if its available for
     * real time test drives, else null.
     *
     * @param modelTreeRef Vehicle identifier.
     */
    private getVehicle(modelTreeRef: string): Observable<Product | undefined> {
        return this.productService.getListings().pipe(
            map(serieses => {
                const series = serieses.find(s => s.items.find(subProduct => subProduct.modelTreeRef === modelTreeRef));

                if (!series) {
                    return null;
                } else {
                    return series.items.find(subProduct => subProduct.modelTreeRef === modelTreeRef);
                }
            })
        );
    }
    /**
     * Given a vehicle, return a string that links to the old booking form.
     */
    private buildOldFormUrl(vehicle: Product) {
        return [
            this.appSettings.retailerBookingFormUrl,
            `?modelTreeReference=${encodeURIComponent(vehicle.modelTreeRef)}`,
            `&carName=${vehicle.combinedModelName}`
        ].join('');
    }
}
